// DS: C25C
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import uniqueId from 'lodash/uniqueId';
import { PapiUser } from '@wix/da-papi-types';
import { useOnOutsideClick } from '@wix/da-hooks/pkg/useOnOutsideClick';
import VisuallyHidden from '@wix/da-a11y/pkg/components/VisuallyHidden';
import UserAvatar from '../User/Avatar/UserAvatar';
import PositionOnlyPopperPopover from '@wix/da-ds/pkg/popper/presets/PositionOnlyPopperPopover';
import StandardMenu from '@wix/da-ds/pkg/Dropdown/MenuPreset/StandardMenu';
import CloseBig from '@wix/da-ds/pkg/Icons/CloseBig';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import s from './UserSearchField.scss';

const noop = () => {};

export interface Props {
  placeholder?: string;
  excludedUsers?: PapiUser[];
  className?: string;
  withClearButton?: boolean;
  inputClassName?: string;
  popupFixedPosition?: boolean;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  // hardcoded list of users to search through
  candidates?: PapiUser[];
  // otherwise search through friends
  onSearch: (
    query: string,
    callback: (results: PapiUser[]) => void,
    options: {
      isChatContext?: boolean;
      userType?: 'both' | 'group' | 'deviant';
      excludeBanned?: boolean;
    }
  ) => void;
  onUserSelect: (user: PapiUser) => void;
  onNoMatchingUsers?: (input: string) => void;
  onBlur?: () => void;
  onClear?: () => void;
  onInputKeyDown?: (e: React.KeyboardEvent) => void;
  usePortal?: boolean;
  /* This is temp flag for controlled release of chat T43614 */
  isChatContext?: boolean;
  disabled?: boolean;
}

const UserSearchField: React.FC<Props> = ({
  placeholder,
  onUserSelect,
  onSearch,
  onNoMatchingUsers,
  candidates,
  onBlur = noop,
  excludedUsers,
  className,
  inputClassName,
  withClearButton,
  popupFixedPosition: popupPositionFixed,
  inputRef: inputRefFromProps,
  onClear = noop,
  onInputKeyDown,
  usePortal = true,
  isChatContext,
  disabled,
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [highlightedMenuItemIndex, setHighlightedMenuItemIndex] = useState(0);
  const inputRefDefault = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [matchingUsers, setMatchingUsers] = useState<PapiUser[]>([]);
  const inputRef = inputRefFromProps || inputRefDefault;
  const { current: dropdownId } = useRef(uniqueId('dropdown'));
  // focus input on mount
  useEffect(() => {
    inputRef.current && inputRef.current.focus();
  }, [inputRef]);

  const onClearClick = useCallback(() => {
    setInputValue('');
    onClear();
  }, [onClear]);

  const onOutsideClick = useCallback(() => {
    setInputValue('');
    setMatchingUsers([]);
    onBlur();
  }, [onBlur]);

  const onSelect = useCallback(
    (user: PapiUser) => {
      setInputValue('');
      setMatchingUsers([]);
      onUserSelect(user);
      inputRef.current && inputRef.current.focus();
    },
    [onUserSelect, inputRef]
  );

  useOnOutsideClick([rootRef, menuRef, inputRef], onOutsideClick);

  const removeExcludedUsers = useCallback(
    found => {
      const newMatchingUsers = excludedUsers
        ? found.filter(
            u =>
              !excludedUsers.some(
                u2 => u2.userId === u.userId || u2.useridUuid === u.useridUuid
              )
          )
        : found;
      if (newMatchingUsers.length === 0) {
        onNoMatchingUsers?.(inputValue);
      }
      setMatchingUsers(newMatchingUsers);
    },
    [excludedUsers, onNoMatchingUsers, inputValue]
  );

  // set search query
  useEffect(() => {
    // we need a minimum of 3 search characters
    if (inputValue.length >= 3) {
      if (candidates) {
        const foundUsers = candidates.filter(({ username }) =>
          username.toLowerCase().startsWith(inputValue.toLowerCase())
        );
        removeExcludedUsers(foundUsers);
      } else {
        onSearch(inputValue, removeExcludedUsers, {
          isChatContext,
          userType: isChatContext ? 'deviant' : undefined,
          excludeBanned: true,
        });
      }
    } else {
      setMatchingUsers([]);
    }
  }, [onSearch, inputValue, candidates, removeExcludedUsers, isChatContext]);

  // arrow keys for menu navigation
  const onRootKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (matchingUsers.length === 0) {
        return;
      }
      switch (e.key) {
        case 'ArrowUp':
          setHighlightedMenuItemIndex(
            highlightedMenuItemIndex === 0
              ? matchingUsers.length - 1
              : highlightedMenuItemIndex - 1
          );
          break;
        case 'ArrowDown':
          setHighlightedMenuItemIndex(
            highlightedMenuItemIndex === matchingUsers.length - 1
              ? 0
              : highlightedMenuItemIndex + 1
          );
          break;
        case 'Enter':
          if (highlightedMenuItemIndex < matchingUsers.length) {
            onSelect(matchingUsers[highlightedMenuItemIndex]);
          }
          break;
        case 'Tab':
          e.preventDefault();
          if (matchingUsers.length > 0) {
            onSelect(matchingUsers[0]);
          }
          break;
        default:
          // Don't handle anything else
          break;
      }
    },
    [highlightedMenuItemIndex, matchingUsers, onSelect]
  );

  // Handle input key events and relay navigation keys to root handler
  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Relay navigation keys to root handler when dropdown is open
      if (
        matchingUsers.length > 0 &&
        ['ArrowUp', 'ArrowDown', 'Enter', 'Tab'].includes(e.key)
      ) {
        onRootKeyDown(e);
        return;
      }

      // Call the original onInputKeyDown if provided
      onInputKeyDown && onInputKeyDown(e);
    },
    [matchingUsers.length, onRootKeyDown, onInputKeyDown]
  );

  const menuItems = useMemo(
    () =>
      matchingUsers.map((user, index) => ({
        value: user.userId,
        highlighted: index === highlightedMenuItemIndex,
        onMouseEnter: () => setHighlightedMenuItemIndex(index),
        onClick: () => {
          onSelect(user);
        },

        label: (
          <>
            <UserAvatar
              user={user}
              size={26}
              className={s['autocomplete-avatar']}
              withLink={false}
            />
            <span>{user.username}</span>
          </>
        ),
      })),
    [highlightedMenuItemIndex, matchingUsers, onSelect]
  );

  return (
    <PositionOnlyPopperPopover
      className={classnames(
        s['root'],
        withClearButton && s['with-clear-button'],
        className
      )}
      ref={rootRef}
      onKeyDown={onRootKeyDown}
      popoverContent={
        <StandardMenu
          id={dropdownId}
          ref={menuRef}
          itemClassName={s['autocomplete-result']}
          items={menuItems}
        />
      }
      placement="bottom"
      secondaryPlacement="start"
      isVisible={matchingUsers.length > 0}
      usePortal={usePortal}
      positionFixed={popupPositionFixed}
    >
      <input
        ref={inputRef}
        type="text"
        className={classnames(s['username-input'], inputClassName)}
        value={inputValue}
        onChange={e => setInputValue(e.target.value)}
        onKeyDown={handleInputKeyDown}
        placeholder={placeholder}
        disabled={disabled}
      />
      {withClearButton && (
        <PlainButtonOrLink className={s['clear']} onClick={onClearClick}>
          <VisuallyHidden>{t('userSearchField.clear')}</VisuallyHidden>
          <CloseBig size={IconSize.TINY} className={s['clear-icon']} />
        </PlainButtonOrLink>
      )}
    </PositionOnlyPopperPopover>
  );
};
UserSearchField.displayName = 'UserSearchField';

export default UserSearchField;
